import { useEffect, useRef, useState } from 'react'
import Modal from '../../components/common/Modal'
import ReadOnlyGuard from '../../components/common/ReadOnlyGuard'
import { dienstKeuzes } from '../../helpers/wervingsacties/resolveWervingsDiensten'
import { UITKOMST_OPTIONS, EMPTY_REGISTRATIE, UITKOMST_MET_DIENSTEN, detailOptionsFor, registratieFormFrom, woningtypeOptionsFor } from '../../helpers/wervingsacties/registratieOptions'

/**
 * @param {object} props
 * @param {boolean} props.modalOpen - Of het venster open is
 * @param {(open: boolean) => void} props.setModalOpen - Opent of sluit het venster
 * @param {object | null} props.registratie - De Wervingsregistratie die bewerkt wordt
 * @param {(form: object) => Promise<void>} props.onSave - Krijgt het formulier bij 'Opslaan'; gooit bij een fout
 * @param {Array<{ key: string, label: string }>} props.diensten - De zichtbare diensten van de organisatie (resolveWervingsDiensten)
 * @param {Object<string, string>} props.dienstNamen - Sleutel → naam, ook van verborgen diensten (dienstNamen)
 */
const RegistratieModal = ({ modalOpen, setModalOpen, registratie, onSave, diensten = [], dienstNamen = {} }) => {
    const [form, setForm] = useState(EMPTY_REGISTRATIE)
    const [bezig, setBezig] = useState(false)
    const [fout, setFout] = useState('')
    // Stopt een tweede klik vóórdat React opnieuw rendert. Met 'automatisch
    // aanmelden' maakte een dubbele klik anders twee huishoudens.
    const bezigRef = useRef(false)

    useEffect(() => {
        if (!modalOpen) return
        setForm(registratieFormFrom(registratie))
        setFout('')
    }, [modalOpen, registratie])

    const opslaan = async () => {
        if (bezigRef.current) return
        bezigRef.current = true
        setBezig(true)
        setFout('')
        try {
            await onSave(form)
        } catch (err) {
            setFout(err?.message || String(err))
        } finally {
            bezigRef.current = false
            setBezig(false)
        }
    }

    const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }))

    // Een datum invullen zet de stap zelf aan: een datum zonder vinkje telt in
    // het overzicht niet mee. Een datum wissen zet niets uit.
    /**
     * @param {'DatumBriefGepost' | 'Datum'} datumVeld
     * @param {'BriefGepost' | 'Deurgesprek'} stapVeld
     * @param {string} value - Datum als yyyy-mm-dd, of '' na wissen
     */
    const setDatum = (datumVeld, stapVeld, value) => setForm((prev) => ({
        ...prev,
        [datumVeld]: value,
        [stapVeld]: value ? true : prev[stapVeld],
    }))

    // Changing the outcome clears the detail if it no longer belongs to the new outcome.
    const onUitkomst = (value) => {
        setForm((prev) => {
            const stillValid = detailOptionsFor(value).some((o) => o.value === prev.UitkomstDetail)
            return { ...prev, Uitkomst: value, UitkomstDetail: stillValid ? prev.UitkomstDetail : '' }
        })
    }

    const toggleDienst = (key) => setForm((prev) => ({
        ...prev,
        Diensten: prev.Diensten.includes(key) ? prev.Diensten.filter((k) => k !== key) : [...prev.Diensten, key],
    }))

    // De zichtbare diensten, plus een verborgen dienst die deze rij al draagt:
    // die moet je nog kunnen uitzetten.
    const keuzes = dienstKeuzes(diensten, form.Diensten, dienstNamen)

    const detailOptions = detailOptionsFor(form.Uitkomst)
    const adresLabel = registratie ? [registratie.Straatnaam, registratie.Huisnummer].filter(Boolean).join(' ') : ''

    const content = (
        <ReadOnlyGuard edit='housevisits.edit'>
            <div className='materials-modal-form'>
                <div className='materials-modal-field-row'>
                    <div className='materials-modal-field'>
                        <label><b>Brief gepost</b></label>
                        <button
                            type='button'
                            className={form.BriefGepost ? 'wa-toggle wa-toggle-on' : 'wa-toggle'}
                            onClick={() => set('BriefGepost', !form.BriefGepost)}
                        >
                            {form.BriefGepost ? 'Ja' : 'Nee'}
                        </button>
                    </div>
                    <div className='materials-modal-field'>
                        <label htmlFor='wa-registratie-datum-brief'><b>Datum brief gepost</b></label>
                        <input
                            id='wa-registratie-datum-brief'
                            type='date'
                            value={form.DatumBriefGepost}
                            onChange={(e) => setDatum('DatumBriefGepost', 'BriefGepost', e.target.value)}
                        />
                    </div>
                </div>

                <div className='materials-modal-field-row'>
                    <div className='materials-modal-field'>
                        <label><b>Deurgesprek</b></label>
                        <button
                            type='button'
                            className={form.Deurgesprek ? 'wa-toggle wa-toggle-on' : 'wa-toggle'}
                            onClick={() => set('Deurgesprek', !form.Deurgesprek)}
                        >
                            {form.Deurgesprek ? 'Ja' : 'Nee'}
                        </button>
                    </div>
                    <div className='materials-modal-field'>
                        <label htmlFor='wa-registratie-datum-gesprek'><b>Datum deurgesprek</b></label>
                        <input
                            id='wa-registratie-datum-gesprek'
                            type='date'
                            value={form.Datum}
                            onChange={(e) => setDatum('Datum', 'Deurgesprek', e.target.value)}
                        />
                    </div>
                </div>

                <div className='materials-modal-field'>
                    <label htmlFor='wa-registratie-uitkomst'><b>Uitkomst</b></label>
                    <select id='wa-registratie-uitkomst' value={form.Uitkomst} onChange={(e) => onUitkomst(e.target.value)}>
                        <option value=''>-- Selecteer --</option>
                        {UITKOMST_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                </div>

                {detailOptions.length > 0 && (
                    <div className='materials-modal-field'>
                        <label><b>Toelichting uitkomst</b></label>
                        <select value={form.UitkomstDetail} onChange={(e) => set('UitkomstDetail', e.target.value)}>
                            <option value=''>-- Selecteer --</option>
                            {detailOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </div>
                )}

                {form.Uitkomst === UITKOMST_MET_DIENSTEN && keuzes.length > 0 && (
                    <div className='materials-modal-field'>
                        <label><b>Doet mee met</b></label>
                        <div className='wa-diensten'>
                            {keuzes.map((d) => {
                                const aan = form.Diensten.includes(d.key)
                                return (
                                    <button
                                        key={d.key}
                                        type='button'
                                        className={aan ? 'wa-toggle wa-toggle-on' : 'wa-toggle'}
                                        aria-pressed={aan}
                                        onClick={() => toggleDienst(d.key)}
                                    >
                                        {d.label}
                                    </button>
                                )
                            })}
                        </div>
                    </div>
                )}

                <div className='materials-modal-field'>
                    <label><b>Woningtype</b></label>
                    <select value={form.Woningtype} onChange={(e) => set('Woningtype', e.target.value)}>
                        <option value=''>-- Selecteer --</option>
                        {woningtypeOptionsFor(form.Woningtype).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                </div>

                <div className='materials-modal-field-row'>
                    <div className='materials-modal-field'>
                        <label><b>E-mail</b></label>
                        <input type='email' value={form.Email} onChange={(e) => set('Email', e.target.value)} />
                    </div>
                    <div className='materials-modal-field'>
                        <label><b>Telefoon</b></label>
                        <input type='text' value={form.Telefoon} onChange={(e) => set('Telefoon', e.target.value)} />
                    </div>
                </div>

                <div className='materials-modal-field'>
                    <label><b>Opmerkingen</b></label>
                    <textarea rows={3} value={form.Opmerkingen} onChange={(e) => set('Opmerkingen', e.target.value)} />
                </div>

                {fout && (
                    <p className='wa-error' role='alert'>{`Opslaan is niet gelukt (${fout}). Probeer het opnieuw.`}</p>
                )}
            </div>
        </ReadOnlyGuard>
    )

    return (
        <Modal
            modalOpen={modalOpen}
            setModalOpen={setModalOpen}
            confirm={opslaan}
            confirmDisabled={bezig}
            pageHeader={adresLabel ? `Registratie — ${adresLabel}` : 'Registratie'}
            content={content}
        />
    )
}

export default RegistratieModal
